/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, Tooltip, Typography } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { StatCardV2 } from '../../../components/v2';
import {
  CATEGORIA_STAT_COLORS,
  EMPTY_KPIS,
  TIPO_NOTA_DEFS,
  formatBRL,
  formatPercent,
} from './constants';

const TOTAL_KEY = '__TOTAL__';

const SUMMARY_CARDS = [
  {
    key: TOTAL_KEY,
    kpiKey: 'totalGasto',
    label: 'Total Gasto',
    icon: <AttachMoneyIcon />,
    color: 'primary',
    filterable: false,
    format: formatBRL,
    valueFontSize: '1.5rem',
  },
  {
    key: 'viaAporte',
    kpiKey: 'viaAporte',
    label: 'Via Aporte',
    icon: <AccountBalanceWalletIcon />,
    color: 'info',
    filterable: false,
    format: formatBRL,
  },
  {
    key: 'viaCartao',
    kpiKey: 'viaCartao',
    label: 'Via Cartão',
    icon: <CreditCardIcon />,
    color: 'success',
    filterable: false,
    format: formatBRL,
  },
  {
    key: 'quantidadeNotas',
    kpiKey: 'quantidadeNotas',
    label: 'Qtd. Notas',
    icon: <ReceiptLongIcon />,
    color: 'default',
    filterable: false,
    format: (v) => String(v ?? 0),
  },
  {
    key: 'ticketMedio',
    kpiKey: 'ticketMedio',
    label: 'Ticket Médio',
    icon: <ShoppingCartOutlinedIcon />,
    color: 'default',
    filterable: false,
    format: formatBRL,
    nullHint: 'Indisponível sem notas no período',
  },
  {
    key: 'percentualInspecao',
    kpiKey: 'percentualInspecao',
    label: '% Inspeção',
    icon: <FactCheckOutlinedIcon />,
    color: 'warning',
    filterable: false,
    format: formatPercent,
    nullHint: 'Sem gastos de inspeção no período',
  },
];

const KpiStrip = ({
  kpis,
  categoryKpis,
  loading,
  activeCategoria,
  onSelectCategoria,
}) => {
  const data = kpis || EMPTY_KPIS;
  const categoryData = categoryKpis || data;

  const handleClick = (card) => {
    if (!card.filterable || !onSelectCategoria) return;
    onSelectCategoria(activeCategoria === card.key ? null : card.key);
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {SUMMARY_CARDS.map((card) => {
          const rawValue = data[card.kpiKey];
          const isNullMetric = rawValue == null && card.nullHint;
          const displayValue = isNullMetric ? '—' : card.format(rawValue ?? 0);

          const cardNode = (
            <StatCardV2
              label={card.label}
              value={displayValue}
              icon={card.icon}
              color={card.color}
              loading={loading}
              valueFontSize={card.valueFontSize}
            />
          );

          return (
            <Grid key={card.key} size={{ xs: 6, sm: 4, md: 2 }}>
              {isNullMetric ? (
                <Tooltip title={card.nullHint} arrow placement="top">
                  <span>{cardNode}</span>
                </Tooltip>
              ) : (
                cardNode
              )}
            </Grid>
          );
        })}
      </Grid>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 2, mb: 1, fontWeight: 600, textTransform: 'uppercase' }}
      >
        Por categoria — clique para filtrar o painel
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          maxHeight: { xs: 120, sm: 'none' },
          overflowY: { xs: 'auto', sm: 'visible' },
        }}
      >
        {TIPO_NOTA_DEFS.map((def) => {
          const rawValue = categoryData[def.kpiKey];
          const isActive = activeCategoria === def.label;
          return (
            <Box key={def.enum} sx={{ minWidth: { xs: '47%', sm: 140, md: 120 }, flex: { sm: '0 0 auto' } }}>
              <StatCardV2
                label={def.label}
                value={formatBRL(rawValue ?? 0)}
                color={CATEGORIA_STAT_COLORS[def.label] || 'default'}
                loading={loading}
                active={isActive}
                activeLabel={isActive ? 'Filtro ativo — clique para remover' : undefined}
                valueFontSize="1.1rem"
                onClick={() => handleClick({ key: def.label, filterable: true })}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

KpiStrip.propTypes = {
  kpis: PropTypes.object,
  categoryKpis: PropTypes.object,
  loading: PropTypes.bool,
  activeCategoria: PropTypes.string,
  onSelectCategoria: PropTypes.func,
};

KpiStrip.defaultProps = {
  kpis: EMPTY_KPIS,
  categoryKpis: null,
  loading: false,
  activeCategoria: null,
  onSelectCategoria: null,
};

export default KpiStrip;
